import React from 'react';
import { ClipboardText, WarningCircle } from '@phosphor-icons/react';

const STATUS_LABELS = {
  new: 'New', open: 'Open', escalated: 'Escalated', acknowledged: 'Acknowledged',
  in_progress: 'In Progress', pending_customer_contact: 'Pending Customer Contact',
  dept_confirmed: 'Dept. Confirmed', resolved: 'Resolved', closed: 'Closed', reopened: 'Reopened',
  dept_action_pending: 'Dept. Action Pending', pending_third_party: 'Pending 3rd Party',
  pending_finance: 'Pending Finance',
};

// Status → next-action banner copy + Editorial token tint. External-facing text.
const NEXT_ACTION = {
  new:                      { label: 'Ticket received — being handled by CS team',       tone: 'under-process' },
  acknowledged:             { label: 'Ticket received — being handled by CS team',       tone: 'under-process' },
  escalated:                { label: 'Awaiting department response',                     tone: 'pending-finance' },
  dept_action_pending:      { label: 'Department action in progress',                    tone: 'pending-finance' },
  dept_confirmed:           { label: 'Department confirmed — awaiting customer contact', tone: 'resolved' },
  pending_customer_contact: { label: 'Agent contacting customer',                        tone: 'pending-finance' },
  pending_finance:          { label: 'Awaiting finance confirmation',                    tone: 'pending-finance' },
  in_progress:              { label: 'Resolution in progress',                           tone: 'under-process' },
  resolved:                 { label: 'Resolved — awaiting closure confirmation',         tone: 'resolved' },
  closed:                   { label: 'Ticket closed',                                    tone: 'closed' },
  reopened:                 { label: 'Ticket reopened — under review',                   tone: 'escalated' },
};

// Editorial tone class map — pastel /8 background + matching text/border.
const TONE_CLS = {
  'under-process':   'border-status-under-process/30 bg-status-under-process/8 text-status-under-process',
  'pending-finance': 'border-status-pending-finance/30 bg-status-pending-finance/8 text-status-pending-finance',
  'resolved':        'border-status-resolved/30 bg-status-resolved/8 text-status-resolved',
  'escalated':       'border-status-escalated/30 bg-status-escalated/8 text-status-escalated',
  'closed':          'border-paper-border bg-paper-muted text-ink-muted',
};

// Status chip (small pill next to the ticket meta).
const STATUS_CHIP_CLS = {
  new: 'bg-status-new/10 text-status-new',
  open: 'bg-status-open/10 text-ink-muted',
  acknowledged: 'bg-status-open/10 text-ink-muted',
  escalated: 'bg-status-escalated/12 text-status-escalated',
  reopened: 'bg-status-escalated/12 text-status-escalated',
  under_process: 'bg-status-under-process/10 text-status-under-process',
  in_progress: 'bg-status-under-process/10 text-status-under-process',
  dept_action_pending: 'bg-status-pending-finance/12 text-status-pending-finance',
  pending_customer_contact: 'bg-status-pending-finance/12 text-status-pending-finance',
  pending_finance: 'bg-status-pending-finance/12 text-status-pending-finance',
  pending_third_party: 'bg-status-pending-finance/12 text-status-pending-finance',
  resolved: 'bg-status-resolved/12 text-status-resolved',
  dept_confirmed: 'bg-status-resolved/12 text-status-resolved',
  closed: 'bg-status-closed/10 text-ink-muted',
};

const PRIORITY_CLS = {
  critical: 'bg-status-escalated/12 text-status-escalated',
  high:     'bg-status-pending-finance/12 text-status-pending-finance',
  medium:   'bg-status-pending-finance/8 text-status-pending-finance',
  low:      'bg-status-resolved/12 text-status-resolved',
};

const fmtDate = (d) => d
  ? new Date(d).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  : '—';

// "delivery-supervisor" → "Delivery Supervisor"
const deptLabel = (slug) => slug
  ? slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  : '—';

// ── small helpers ──
function Card({ title, children }) {
  return (
    <section className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft sm:p-6">
      {title && (
        <div className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">{title}</div>
      )}
      {children}
    </section>
  );
}

function MetaCell({ label, children }) {
  return (
    <div>
      <div className="mb-0.5 text-[11px] text-ink-faint">{label}</div>
      <div className="text-sm font-medium text-ink" dir="auto">{children || '—'}</div>
    </div>
  );
}

export default function MagicLinkViewView({ ticket, notes, history, loading, error, entityName }) {
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper text-sm text-ink-muted">
        Loading ticket…
      </div>
    );
  }
  if (error || !ticket) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper p-6">
        <div className="flex max-w-md items-start gap-3 rounded-card border border-status-escalated/30 bg-status-escalated/8 px-5 py-4 text-sm text-status-escalated">
          <WarningCircle size={20} weight="fill" className="mt-0.5 flex-none" />
          <div>
            <div className="font-semibold">Link unavailable</div>
            <div className="mt-1 text-ink-muted">{error || 'This link is invalid or has expired.'}</div>
          </div>
        </div>
      </div>
    );
  }

  const nextAction = NEXT_ACTION[ticket.status] || { label: STATUS_LABELS[ticket.status] || ticket.status, tone: 'closed' };
  const statusChipCls = STATUS_CHIP_CLS[ticket.status] || 'bg-paper-muted text-ink-muted';
  const priorityChipCls = PRIORITY_CLS[ticket.complaint_priority] || 'bg-paper-muted text-ink-muted';

  return (
    <div className="min-h-screen bg-paper font-sans text-ink">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        {/* Wordmark bar */}
        <div className="mb-6">
          <div className="font-serif text-2xl text-ink">Aralsane</div>
          <div className="text-xs text-ink-faint">Ticket view — read-only.</div>
        </div>

        {/* Header */}
        <div className="mb-5">
          <div className="mb-2 text-xs text-ink-faint">Ref · {ticket.ticket_number || '—'}</div>
          <h1 className="font-serif text-2xl leading-snug text-ink sm:text-3xl" dir="auto">
            {ticket.subject || ticket.complaint_type || 'Ticket'}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-chip px-2.5 py-1 text-[11px] font-semibold ${statusChipCls}`}>
              {STATUS_LABELS[ticket.status] || ticket.status}
            </span>
            {ticket.complaint_priority && (
              <span className={`inline-flex items-center rounded-chip px-2.5 py-1 text-[11px] font-semibold capitalize ${priorityChipCls}`}>
                {ticket.complaint_priority}
              </span>
            )}
            {ticket.escalated_to_dept && (
              <span className="inline-flex items-center rounded-chip bg-paper-muted px-2.5 py-1 text-[11px] font-semibold text-ink-muted">
                → {deptLabel(ticket.escalated_to_dept)}
              </span>
            )}
          </div>
        </div>

        {/* Next-action banner (status-toned) */}
        <div className={`mb-6 flex items-center gap-2.5 rounded-field border px-4 py-3 text-sm font-semibold ${TONE_CLS[nextAction.tone]}`}>
          <ClipboardText size={16} weight="fill" />
          <span>{nextAction.label}</span>
        </div>

        <div className="flex flex-col gap-4">
          {/* Ticket Information */}
          <Card title="Ticket Information">
            <div className="grid grid-cols-1 gap-x-6 gap-y-3.5 sm:grid-cols-2">
              <MetaCell label="Complaint Group">{ticket.complaint_group}</MetaCell>
              <MetaCell label="Complaint Type">{ticket.complaint_type}</MetaCell>
              <MetaCell label="Channel">
                {ticket.forwarded_by ? (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    {ticket.channel_source || '—'}
                    <span className="rounded-chip bg-paper-muted px-1.5 py-0.5 text-[10px] font-medium text-ink-muted">
                      ↪ Forwarded by {ticket.forwarded_by}
                    </span>
                  </span>
                ) : (ticket.channel_source || '—')}
              </MetaCell>
              <MetaCell label="Priority">{ticket.complaint_priority}</MetaCell>
              <MetaCell label="SAP Order Ref">{ticket.sap_order_reference}</MetaCell>
              <MetaCell label="Customer Name">{ticket.customer_name}</MetaCell>
              <MetaCell label="Customer Mobile">{ticket.customer_mobile}</MetaCell>
              <MetaCell label="Customer Email">{ticket.customer_email}</MetaCell>
              <MetaCell label="Created">{fmtDate(ticket.created_at)}</MetaCell>
              <MetaCell label="Last Updated">{fmtDate(ticket.updated_at)}</MetaCell>
            </div>
          </Card>

          {/* Original Message — RTL-aware */}
          {ticket.original_message && (() => {
            const isRtl = /[؀-ۿ]/.test(ticket.original_message);
            return (
              <Card title="Original Message (as received)">
                <div className={`relative ${isRtl ? 'pr-3' : 'pl-3'}`}>
                  <div className={`absolute ${isRtl ? 'right-0' : 'left-0'} top-0 bottom-0 w-0.5 rounded-full bg-status-under-process/40`} />
                  <p
                    dir={isRtl ? 'rtl' : 'ltr'}
                    className={`whitespace-pre-wrap text-sm leading-relaxed text-ink-muted ${isRtl ? 'text-right' : ''}`}>
                    {ticket.original_message}
                  </p>
                </div>
              </Card>
            );
          })()}

          {/* Description */}
          {ticket.complaint_description && (
            <Card title="Description">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-muted" dir="auto">
                {ticket.complaint_description}
              </p>
            </Card>
          )}

          {/* Photos */}
          {ticket.complaint_photo && (
            <Card title="Attached Photos">
              <div className="flex flex-wrap gap-2.5">
                {ticket.complaint_photo.split(',').map((p, i) => {
                  const clean = p.trim();
                  if (!clean) return null;
                  const url = clean.startsWith('http') ? clean : clean;
                  return (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                      <img
                        src={url}
                        alt={`Attachment ${i + 1}`}
                        className="h-24 w-32 rounded-field border border-paper-border object-cover transition hover:opacity-90"
                      />
                    </a>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Status History */}
          {Array.isArray(history) && history.length > 0 && (
            <Card title="Status History">
              <ol className="flex flex-col">
                {history.map((h, i) => (
                  <li key={h.id || i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="mt-1 h-2.5 w-2.5 flex-none rounded-full bg-paper-border2" />
                      {i < history.length - 1 && <span className="w-0.5 flex-1 bg-paper-border2" />}
                    </div>
                    <div className="pb-4">
                      <div className="text-sm text-ink">
                        {h.old_status
                          ? <>{STATUS_LABELS[h.old_status] || h.old_status} → <strong className="font-semibold">{STATUS_LABELS[h.new_status] || h.new_status}</strong></>
                          : <><strong className="font-semibold">Ticket created</strong> ({STATUS_LABELS[h.new_status] || h.new_status})</>}
                      </div>
                      {h.note && <div className="mt-0.5 text-xs text-ink-muted" dir="auto">{h.note}</div>}
                      <div className="mt-0.5 text-xs text-ink-faint">{fmtDate(h.created_at)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {/* Public-facing notes (if any) — filter to non-internal only. */}
          {Array.isArray(notes) && notes.filter((n) => !n.is_internal).length > 0 && (
            <Card title="Notes">
              <div className="flex flex-col gap-3">
                {notes.filter((n) => !n.is_internal).map((n) => (
                  <div key={n.id} className="rounded-field border border-paper-border bg-paper p-3 text-sm">
                    <div className="mb-1 text-xs text-ink-faint">{fmtDate(n.created_at)}</div>
                    <p className="whitespace-pre-wrap text-ink-muted" dir="auto">{n.note}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="mt-10 text-center text-xs text-ink-faint">
          Aralsane Group{entityName ? ` · ${entityName}` : ''}
        </div>
      </div>
    </div>
  );
}
